import { Component, type ReactNode } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { ProtectedRoute } from '@secureembed/shared-crm'
import { useAuth } from './contexts/AuthContext'
import { AuthProvider } from './contexts/AuthContext'
import { RoleProvider } from './contexts/RoleContext'
import { UIPreferencesProvider } from './contexts/UIPreferencesContext'
import { AggregatorProvider } from './contexts/AggregatorContext'
import { OrgTypeProvider } from './contexts/OrgTypeContext'

// Error boundary to prevent blank pages from unhandled component errors
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[50vh] p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-4 text-2xl">!</div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-sm text-gray-500 mb-4 max-w-md">{this.state.error.message}</p>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload() }}
            className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600"
          >
            Reload Page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
import { ScrollToTop } from './components/ScrollToTop'
import Login from './pages/Login'
import Signup from './pages/Signup'
import LinkedInCallback from './pages/auth/LinkedInCallback'
import RecoveryRequest from './pages/auth/RecoveryRequest'
import ForgotPassword from './pages/auth/ForgotPassword'
import Terms from './pages/legal/Terms'
import Privacy from './pages/legal/Privacy'
import RecoveryConfirm from './pages/auth/RecoveryConfirm'
import ProjectRecoveryConfirm from './components/recovery/ProjectRecoveryConfirm'
import AuthCallback from './pages/auth/AuthCallback'
import LandingPage from './pages/LandingPage'
import Welcome from './pages/Welcome'
import AppShell from './components/shell/AppShell'
import { WorkspaceProvider } from './contexts/WorkspaceContext'
import { LegacyRedirect, LegacyPreferencesRedirect } from './components/shell/LegacyRedirect'
import { LEGACY_REDIRECTS } from './config/navigation'
import { SpineProvider } from './components/spine'
import Dashboard from './pages/inventor/Dashboard'
import ProjectView from './pages/inventor/ProjectView'
import FolderView from './pages/inventor/FolderView'
import DocumentDetail from './pages/inventor/DocumentDetail'
import DocumentUpload from './pages/inventor/DocumentUpload'
import SearchResults from './pages/inventor/SearchResults'
import WhoCanSeeWhatOfMine from './pages/inventor/WhoCanSeeWhatOfMine'
// Connections page removed from nav — connections graph is now inline on Document/Folder/Project views
// import Connections from './pages/inventor/Connections'
import MyLists from './pages/inventor/MyLists'
import ReviewDashboard from './pages/inventor/ReviewDashboard'
// Notifications merged into ReviewDashboard (Workflow page)
// import Notifications from './pages/inventor/Notifications'
import JournalBrowse from './pages/inventor/JournalBrowse'
import Patents from './pages/inventor/Patents'
// PatentBrowse merged into Patents (unified page)
import JournalDetail from './pages/inventor/JournalDetail'
import EcosystemMarketplace from './pages/inventor/EcosystemMarketplace'
import EcosystemProfileDetail from './pages/inventor/EcosystemProfileDetail'
import Settings from './pages/inventor/Settings'
import BillingDashboard from './pages/inventor/BillingDashboard'
import GuidedSubmission from './pages/inventor/GuidedSubmission'
import EncryptionKeys from './pages/inventor/EncryptionKeys'
import CrmOverview from './pages/inventor/crm/CrmOverview'
import ContactsList from './pages/inventor/crm/ContactsList'
import ContactDetail from './pages/inventor/crm/ContactDetail'
import OrganisationsList from './pages/inventor/crm/OrganisationsList'
import OrgDetail from './pages/inventor/crm/OrgDetail'
import MyCrmDashboard from './pages/inventor/crm/MyCrmDashboard'
import MyContacts from './pages/inventor/crm/MyContacts'
import MyOrganisations from './pages/inventor/crm/MyOrganisations'
import MyTodos from './pages/inventor/crm/MyTodos'
import SharedProjects from './pages/collaborator/SharedProjects'
import SharedProjectView from './pages/collaborator/SharedProjectView'
import SharedDocumentView from './pages/collaborator/SharedDocumentView'
import AdminDashboard from './pages/admin/Dashboard'
import UserManagement from './pages/admin/UserManagement'
import AuditReports from './pages/admin/AuditReports'
import SecurityReports from './pages/admin/SecurityReports'
import Forbidden from './pages/Forbidden'
import KeyRevokedBanner from './components/encryption/KeyRevokedBanner'
import CohortInviteAccept from './pages/cohort/CohortInviteAccept'
import { TeamManagement } from './pages/aggregator/TeamManagement'
import OrganisationsManagement from './pages/admin/OrganisationsManagement'
import MembersAndLicences from './pages/organisation/MembersAndLicences'
import ProjectFolderAccess from './pages/organisation/ProjectFolderAccess'
import IdentitySSO from './pages/organisation/IdentitySSO'
import OrgEncryptionKeys from './pages/organisation/OrgEncryptionKeys'
import Preferences from './pages/organisation/Preferences'
import OrgOverview from './pages/organisation/OrgOverview'
import AggregatorPortfolio from './pages/aggregator/AggregatorPortfolio'
import EntityGroupView from './pages/aggregator/EntityGroupView'
import AcrossPortfolios from './pages/aggregator/AcrossPortfolios'
import AggregatorInbound from './pages/aggregator/AggregatorInbound'
import KeypairSetup from './pages/inventor/KeypairSetup'
import Support from './pages/Support'
import AdminSupport from './pages/AdminSupport'

function AdminGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading, user } = useAuth()
  return (
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      isLoading={loading}
      userGroups={user?.groups}
      requiredGroup="innovateflow-admin"
    >
      {children}
    </ProtectedRoute>
  )
}

// Story #6760: /search sits inside the shared shell. Signed out, it must not
// draw the shell (and with it the last person's recent searches); it goes to
// sign-in instead.
function SignedInGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  return (
    <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={loading}>
      {children}
    </ProtectedRoute>
  )
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <RoleProvider>
        <AggregatorProvider>
        <OrgTypeProvider>
        <UIPreferencesProvider>
        <WorkspaceProvider>
        {/* The spine: a graduated page declares where the viewer is (useSetSpine)
            and the shell renders the focus rail and the one crumb line for it. */}
        <SpineProvider>
        <ScrollToTop />
        {/* Story #6490 8.3 — names the paused workspace on a key-status 403. */}
        <KeyRevokedBanner />
        <Routes>
          {/* New marketing welcome page for visitors arriving from marketing materials */}
          <Route path="/welcome" element={<Welcome />} />
          {/* Existing landing page, unchanged — now served at /home (the auth/redirect target) */}
          <Route path="/home" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          {/* BUG-099: LinkedIn OAuth popup landing — relays code to opener */}
          <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
          {/* MFA — public recovery + SSO callback landings (D15/R17, D10) */}
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          {/* UX-003 / D5: public placeholder legal pages (wording from Rob or counsel) */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/auth/recovery" element={<RecoveryRequest />} />
          <Route path="/auth/recovery/confirm" element={<RecoveryConfirm />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/cohort/accept" element={<CohortInviteAccept />} />
          <Route element={<AppShell />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/projects" element={<Dashboard />} />
            <Route path="/recovery/project" element={<ProjectRecoveryConfirm />} />
            <Route path="/project/:projectId" element={<ProjectView />} />
            <Route path="/project/:projectId/folder/:folderId" element={<FolderView />} />
            <Route path="/document/:documentId" element={<DocumentDetail />} />
            <Route path="/upload" element={<DocumentUpload />} />
            {/* S2-03: /new is the canonical wizard route (the sidebar links here);
                /submit is kept as a redirect so old inbound links still resolve. */}
            <Route path="/submit" element={<Navigate to="/new" replace />} />
            <Route path="/new" element={<GuidedSubmission />} />
            <Route path="/search" element={<SignedInGuard><SearchResults /></SignedInGuard>} />
            <Route path="/connections" element={<Navigate to="/" replace />} />
            <Route path="/patents" element={<Patents />} />
            <Route path="/patents/*" element={<Patents />} />
            <Route path="/journals" element={<JournalBrowse />} />
            <Route path="/journals/*" element={<JournalDetail />} />
            <Route path="/lists" element={<MyLists />} />
            <Route path="/tasks" element={<ReviewDashboard />} />
            <Route path="/directory" element={<Navigate to="/ecosystem" replace />} />
            <Route path="/ecosystem" element={<EcosystemMarketplace />} />
            <Route path="/ecosystem/:profileId" element={<EcosystemProfileDetail />} />
            {/* Spec §10.9 canonical Account routes */}
            <Route path="/account/settings" element={<Settings />} />
            <Route path="/account/billing" element={<BillingDashboard />} />
            <Route path="/account/security" element={<EncryptionKeys />} />
            <Route path="/account/preferences" element={<Preferences />} />
            {/* Contract rows C2 and C5 (lane B5). A personal route: no
                ROUTE_KIND_PREFIXES entry for /account, because forcing a
                workspace switch here would move a zero-membership inventor
                out of the personal workspace — the very defect this lane
                exists to remove. */}
            <Route path="/account/sharing" element={<WhoCanSeeWhatOfMine />} />
            <Route path="/keypair-setup" element={<KeypairSetup />} />
            {/* In-portal Support ticket system (Story #4613, Streams 8a/8b) */}
            <Route path="/support" element={<Support />} />
            {/* Spec §10.6/§10.7 net-new workspace homes */}
            <Route path="/org/overview" element={<OrgOverview />} />
            {/* Design route inventory uses /org/* and /agg/team as the canonical
                org/aggregator paths. UX-014: every legacy path (/organisation/*,
                /team, /workflow, /settings, /billing, /encryption...) answers with
                a redirect, rendered from LEGACY_REDIRECTS just below. */}
            <Route path="/org/members" element={<MembersAndLicences />} />
            <Route path="/org/access" element={<ProjectFolderAccess />} />
            <Route path="/org/identity" element={<IdentitySSO />} />
            <Route path="/org/keys" element={<OrgEncryptionKeys />} />
            <Route path="/org/preferences" element={<Preferences />} />
            <Route path="/agg" element={<AggregatorPortfolio />} />
            {/* Contract rows S5 and C1 (lane A9). */}
            <Route path="/agg/entity/group/:groupName" element={<EntityGroupView />} />
            {/* Contract row S7 (lane A9) — across portfolios, Scope 4. */}
            <Route path="/agg/across" element={<AcrossPortfolios />} />
            <Route path="/agg/inbound" element={<AggregatorInbound />} />
            <Route path="/agg/team" element={<TeamManagement />} />
            <Route path="/agg/billing" element={<BillingDashboard />} />
            {LEGACY_REDIRECTS.map(([from, to]) => (
              <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
            ))}
            <Route path="/preferences" element={<LegacyPreferencesRedirect />} />
            <Route path="/crm" element={<MyCrmDashboard />} />
            <Route path="/crm/contacts" element={<MyContacts />} />
            <Route path="/crm/organisations" element={<MyOrganisations />} />
            <Route path="/crm/todos" element={<MyTodos />} />
            <Route path="/project/:projectId/crm" element={<CrmOverview />} />
            <Route path="/project/:projectId/crm/contacts" element={<ContactsList />} />
            <Route path="/project/:projectId/crm/contacts/:contactId" element={<ContactDetail />} />
            <Route path="/project/:projectId/crm/organisations" element={<OrganisationsList />} />
            <Route path="/project/:projectId/crm/organisations/:orgId" element={<OrgDetail />} />
            {/* Collaborators are Personal users whose primary content is "Shared with me" (Spec §5.1) */}
            <Route path="/shared" element={<SharedProjects />} />
            <Route path="/shared/project/:projectId" element={<SharedProjectView />} />
            <Route path="/shared/document/:documentId" element={<SharedDocumentView />} />
            {/* Platform Admin — a workspace within the same shell; still group-guarded */}
            <Route path="/admin" element={<AdminGuard><Outlet /></AdminGuard>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="organisations" element={<OrganisationsManagement />} />
              <Route path="audit" element={<AuditReports />} />
              <Route path="security" element={<SecurityReports />} />
              {/* Admin support queue + detail (Story #4613, Stream 8c) */}
              <Route path="support" element={<AdminSupport />} />
              {/* UX-046 (D6): there is no platform settings page; the old path used to show
                  the personal Account page. Send bookmarks to the console home. */}
              <Route path="settings" element={<Navigate to="/admin" replace />} />
            </Route>
          </Route>
          <Route path="/forbidden" element={<Forbidden />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </SpineProvider>
        </WorkspaceProvider>
        </UIPreferencesProvider>
        </OrgTypeProvider>
        </AggregatorProvider>
      </RoleProvider>
    </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
